import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    CategoryCreateBreadcrumbs,
    CountryBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as CountryActions from "../../../actions/countries";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./CountryPage.scss";

interface CountryPageProps {
    form: Dtos.Country & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    country: Dtos.Country;
    lookups: Dtos.Lookup[];
    loadingCountry: boolean;
    loadCountryFailure: boolean;
    loadCountryErrorCode: string;
    savingCountry: boolean;
    saveCountrySuccess: boolean;
    saveCountryFailure: boolean;
    creatingCountry: boolean;
    createCountrySuccess: boolean;
    validatingCountry: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearCountry: CountryActions.ICountryClearActionCreator;
    loadCountry: CountryActions.ICountryLoadByIdActionCreator;
    saveCountry: CountryActions.ICountrySaveActionCreator;
    createCountry: CountryActions.ICountryCreateActionCreator;
    validateCountry: CountryActions.ICountryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.country";

const CountryPage: FC<CountryPageProps> = ({
    form,
    formState,
    formProperties,
    country,
    lookups,
    loadingCountry,
    loadCountryFailure,
    loadCountryErrorCode,
    savingCountry,
    saveCountrySuccess,
    saveCountryFailure,
    creatingCountry,
    createCountrySuccess,
    validatingCountry,
    validationFailures,
    permissions,
    clearCountry,
    loadCountry,
    saveCountry,
    createCountry,
    validateCountry,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { countryId } = useParams<{ countryId: string }>();
    const countryIdNumber = parseInt(countryId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Country = {} as Dtos.Country;

        loadForm(reduxFormName, Object.assign({ ...country }, extraData));
    };


    // Load country
    useEffect(() => {
        if (countryIdNumber && !country && !loadingCountry) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(CountryBreadcrumbs(countryIdNumber));
            loadCountry(countryIdNumber);
        } else if (
            !countryIdNumber &&
            !creatingCountry &&
            !createCountrySuccess
        ) {
            createCountry();
            setBreadcrumbs(CategoryCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (country && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        countryIdNumber,
        country,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        CountryBreadcrumbs,
        loadCountry,
        createCountry,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingCountry,
        creatingCountry,
        createCountrySuccess,
        CategoryCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadCountryFailure) {
            navigate(`/error/${loadCountryErrorCode}`);
        }
    }, [loadCountryFailure, loadCountryErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (countryIdNumber && country?.value) {
            setBreadcrumbs(CountryBreadcrumbs(countryIdNumber, country?.value));
        }
    }, [countryIdNumber, country, setBreadcrumbs, CountryBreadcrumbs]);

    // Reload country after saving
    useEffect(() => {
        if (!savingCountry && saveCountrySuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Country saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/countries";
                navigate(url);
            } else if (!countryIdNumber) {
                let url: string = `/settings/general/countries/${country?.id}`;
                navigate(url);
                clearCountry();
            }
        }
    }, [
        savingCountry,
        saveCountrySuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingCountry && saveCountryFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingCountry, saveCountryFailure]);

    // Cleanup effect to clear the country on component unmount
    useEffect(() => {
        return () => {
            clearCountry();
            resetForm(reduxFormName);
        };
    }, [clearCountry, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/countries";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Country) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Country = {} as Dtos.Country;

            await saveCountry(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Country = {} as Dtos.Country;

            if (form && hasBeenSubmitted && !validatingCountry) {
                await validateCountry(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.CountriesUpdate,
                Dtos.Permission.CountriesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingCountry ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "country",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "country",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "country",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "country",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingCountry,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingCountry}
            permission={Dtos.Permission.CountriesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<CountryPageProps> => {
    let country: Dtos.Country | undefined = !(
        state.countries.formData instanceof Array
    )
        ? state.countries.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        country: country!,
        loadingCountry:
            state.countries.loadState &&
            state.countries.loadState.status == RequestState.Pending,
        loadCountryFailure:
            state.countries.loadState &&
            state.countries.loadState.status == RequestState.Failure,
        loadCountryErrorCode: state.countries.loadState.errorCode,
        savingCountry:
            state.countries.saveState &&
            state.countries.saveState.status == RequestState.Pending,
        saveCountrySuccess:
            state.countries.saveState &&
            state.countries.saveState.status == RequestState.Success,
        saveCountryFailure:
            state.countries.saveState &&
            state.countries.saveState.status == RequestState.Failure,
        creatingCountry:
            state.countries.createState &&
            state.countries.createState.status == RequestState.Pending,
        createCountrySuccess:
            state.countries.createState &&
            state.countries.createState.status == RequestState.Success,
        validatingCountry:
            state.countries.validateState &&
            state.countries.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.country,
        formState: state.countries.formState,
        formProperties: state.countries.formProperties,
        validationFailures: country
            ? state.countries.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<CountryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadCountry: bindActionCreators(
            CountryActions.LoadCountryById,
            dispatch
        ),
        clearCountry: bindActionCreators(CountryActions.Clear, dispatch),
        saveCountry: bindActionCreators(CountryActions.SaveCountry, dispatch),
        createCountry: bindActionCreators(
            CountryActions.CreateCountry,
            dispatch
        ),
        validateCountry: bindActionCreators(
            CountryActions.ValidateCountry,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CountryPage);
